import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { Menu } from "../../components/Menu";
import { MainButton } from "../../components/MainButton";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import { useSeller } from "../../hooks/useSeller";
import Swal from "sweetalert2";
import "./sellerPerfil.css";

// Ícones
import { UserIcon } from "../../components/MenuIcons/UserIcon";
import { GearIcon } from "../../components/MenuIcons/GearIcon";
import { StarIcon } from "../../components/MenuIcons/StarIcon";
import { LightIcon } from "../../components/MenuIcons/LightIcon";
import { ChrevonRightIcon } from "../../components/MenuIcons/ChevrotRight";
import { OrderIcon } from "../../components/MenuIcons/OrderIcon";
import { GiftIcon } from "../../components/MenuIcons/GiftIcon";


type SellerProfileOption = {
    icon: string;
    title: string;
    subtitle: string;
    route: string;
    action?: () => void;
    show: boolean;
};

type SellerProfileSection = {
    name: string;
    options: SellerProfileOption[];
};

type SellerInfo = {
    id: number;
    name: string;
    email: string;
    cpf: string;
    numberPhone: string | null;
    status: boolean;
    image: string;
    allowDiscount?: boolean;
    discountValue?: number;
};

export function SellerPerfil() {
    const navigate = useNavigate();
    const { pluginspace_id } = useParams();
    const { getAppConfig } = useAppConfig();
    const { registerAccess } = usePluginspace();
    const { getTokenSellertoRequest, getSellerUser } = useSeller();

    const [loading, setLoading] = useState(true);
    const [loadingHeader, setLoadingHeader] = useState(true);
    const [config, setConfig] = useState<ConfigType | void>();
    const [sellerInfo, setSellerInfo] = useState<SellerInfo>({
        id: 0,
        name: "",
        email: "",
        cpf: "",
        numberPhone: null,
        status: true,
        image: "default/default-user.jpg",
    });

    const profileSections: SellerProfileSection[] = [
        {
            name: "Minha Conta",
            options: [
                {
                    icon: "user",
                    title: "Informações Pessoais",
                    subtitle: "Confira e edite seus dados cadastrais",
                    route: "/seller-profile/info",
                    action: () => handleNavigateToInfo(),
                    show: true
                },
                {
                    icon: "gear",
                    title: "Configurações da Conta",
                    subtitle: "Preferências e configurações",
                    route: "/seller-profile/settings",
                    action: () => handleNavigateToSettings(),
                    show: false
                },
                {
                    icon: "gear",
                    title: "Alterar Senha",
                    subtitle: "Atualização de senha de acesso",
                    route: "/seller-profile/password",
                    action: () => handleNavigateToPassword(),
                    show: false
                }
            ]
        },
        {
            name: "Vendas",
            options: [
                {
                    icon: "star",
                    title: "Meus Carrinhos",
                    subtitle: "Acompanhe todos os seus carrinhos",
                    route: "/sellerOrder",
                    action: () => navigate(`/${pluginspace_id}/sellerOrder`),
                    show: true
                },
                {
                    icon: "light",
                    title: "Relatórios",
                    subtitle: "Visualize relatórios de vendas",
                    route: "/seller-profile/reports",
                    action: () => handleNavigateToReports(),
                    show: false
                },
                {
                    icon: "qr",
                    title: "Meu QR Code",
                    subtitle: "Compartilhe seu link de vendas",
                    route: "/seller-profile/qrcode",
                    action: () => handleNavigateToQRCode(),
                    show: true
                },
                {
                    icon: "list",
                    title: "Links e Catálogos",
                    subtitle: "Visualize links e catálogos de produtos",
                    route: "/seller-profile/catalogs",
                    action: () => handleNavigateToCatalogs(),
                    show: false
                },
                {
                    icon: "gear",
                    title: "Configurações de Entrega",
                    subtitle: "Configure áreas e taxas de entrega",
                    route: "/seller-profile/delivery",
                    action: () => handleNavigateToDelivery(),
                    show: false
                }
            ]
        }
    ];

    async function getConfig() {
        setLoadingHeader(true);
        setConfig(await getAppConfig());
        setLoadingHeader(false);
        await getSellerInfo();
        setLoading(false);
    }

    async function getSellerInfo() {
        try {
            setLoading(true);

            await getSellerUser().then((user) => {
                if (user) {
                    setSellerInfo((prevInfo) => ({
                        ...prevInfo,
                        id: user.id || prevInfo.id,
                        name: user.name || prevInfo.name,
                        email: user.email || prevInfo.email,
                        cpf: user.cpf || prevInfo.cpf,
                        numberPhone: user.numberPhone || prevInfo.numberPhone,
                        status: true,
                        image: user.image || prevInfo.image,
                    }));
                }
            }).catch((error) => {
                console.error("Erro ao buscar informações do vendedor:", error);
            });
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    }

    const returnIcon = (iconType: string) => {
        switch (iconType) {
            case "user":
                return <UserIcon />;
            case "gear":
                return <GearIcon />;
            case "star":
                return <StarIcon />;
            case "light":
                return <LightIcon />;
            case "qr":
              return <GiftIcon />;
            default:
                return <UserIcon />;
        }
    };

    const handleNavigateToInfo = () => {
        navigate(`/${pluginspace_id}/seller-profile/info`);
    };

    const handleNavigateToSettings = () => {
        Swal.fire({
            icon: "info",
            title: "Em Desenvolvimento",
            text: "Funcionalidade de configurações em desenvolvimento",
        });
    };

    const handleNavigateToPassword = () => {
        Swal.fire({
            icon: "info",
            title: "Em Desenvolvimento",
            text: "Funcionalidade de alteração de senha em desenvolvimento",
        });
    };

    const handleNavigateToCatalogs = () => {
        Swal.fire({
            icon: "info",
            title: "Em Desenvolvimento",
            text: "Funcionalidade de links e catálogos em desenvolvimento",
        });
    };

    const handleNavigateToReports = () => {
        Swal.fire({
            icon: "info",
            title: "Em Desenvolvimento",
            text: "Funcionalidade de relatórios em desenvolvimento",
        });
    };

    const handleNavigateToQRCode = () => {
        navigate(`/${pluginspace_id}/seller-profile/qrcode`);
    };

    const handleNavigateToDelivery = () => {
        Swal.fire({
            icon: "info",
            title: "Em Desenvolvimento",
            text: "Funcionalidade de configurações de entrega em desenvolvimento",
        });
    };

    useEffect(() => {
        registerAccess("seller-profile");
        getConfig();
    }, []);

    function formatDocumentSeller(document: string) {
        if (!document) return "";

        const cleaned = document.replace(/\D/g, "");
        if (cleaned.length === 11) {
            return `CPF: ${cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")}`;
        } else if (cleaned.length === 14) {
            return `CNPJ: ${cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")}`;
        }
        return document;
    }

    return (
        <div className="seller-profile">
            <div className="content-seller-profile">
                {loading ? (
                    <div style={{ width: "100%", height: "calc(100vh)" }}>
                        <Loading
                            type="spinningBubbles"
                            color="#FFF"
                            width={64}
                            height={64}
                        />
                    </div>
                ) : (
                    <div className="seller-profile-container">
                        <div className="title-seller-profile">
                            <h2>Meu Perfil</h2>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => navigate(-1)}
                                style={{ cursor: "pointer" }}
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.978551 2.21657L6.957 8.2395L0.978551 14.2624L2.97137 16.2701L8.94981 10.2471L14.9283 16.2701L16.9211 14.2624L10.9426 8.2395L16.9211 2.21657L14.9283 0.208931L8.94981 6.23186L2.97137 0.208931L0.978551 2.21657Z"
                                    fill="#636363"
                                />
                            </svg>
                        </div>

                        <div className="seller-profile-body">
                            {/* Card com informações do vendedor */}
                            <div className="seller-info-card">
                                <div className="seller-avatar">
                                    <UserIcon />
                                </div>
                                <div className="seller-details">
                                    <h3>{sellerInfo.name}</h3>
                                    <p>{sellerInfo.email}</p>
                                    <p>
                                        {formatDocumentSeller(sellerInfo.cpf)}
                                    </p>
                                    <span className={`status ${sellerInfo.status ? 'ativo' : 'inativo'}`}>
                                        {sellerInfo.status ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                            </div>

                            {/* Seções de opções */}
                            <div className="profile-sections">
                                {profileSections.map((section, sectionIndex) => (
                                    <div key={sectionIndex} className="profile-section">
                                        <h3 className="section-title">{section.name}</h3>
                                        <div className="section-options">
                                            {section.options.filter(option => option.show).map((option, optionIndex) => (
                                                <div
                                                    key={optionIndex}
                                                    className="profile-option"
                                                    onClick={option.action}
                                                >
                                                    <div className="option-icon">
                                                        {returnIcon(option.icon)}
                                                    </div>
                                                    <div className="option-content">
                                                        <h4>{option.title}</h4>
                                                        <p>{option.subtitle}</p>
                                                    </div>
                                                    <div className="option-arrow">
                                                        <ChrevonRightIcon action={() => { }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Botão de logout */}
                            <div className="logout-section">
                                <MainButton
                                    color={
                                        config?.webappcolor.fontbutton
                                            ? config.webappcolor.fontbutton
                                            : "#636363"
                                    }
                                    backgroundColor={
                                        config?.webappcolor.webappbutton
                                            ? config.webappcolor.webappbutton
                                            : "#EAEAEA"
                                    }
                                    text="Sair da Conta"
                                    onClick={() => {
                                        Swal.fire({
                                            title: "Confirmar Logout",
                                            text: "Deseja realmente sair da sua conta?",
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonText: "Sim, sair",
                                            cancelButtonText: "Cancelar",
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                // Implementar logout
                                                navigate(`/${pluginspace_id}/seller`);
                                            }
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {!loadingHeader && (
                    <Menu
                        activeMenu="info"
                        fontColor={
                            config?.webappcolor.iconsfooter
                                ? config?.webappcolor.iconsfooter
                                : "#636363"
                        }
                        activeFontColor={
                            config?.webappcolor.highlightfooter
                                ? config?.webappcolor.highlightfooter
                                : "#000"
                        }
                        backgroundMenu={
                            config?.webappcolor.backgroundfooter
                                ? config?.webappcolor.backgroundfooter
                                : "#F5F5F5"
                        }
                        colorIcons={
                            config?.webappcolor.iconsfooter
                                ? config?.webappcolor.iconsfooter
                                : "#636363"
                        }
                        highlightColorIcons={
                            config?.webappcolor.highlightfooter
                                ? config?.webappcolor.highlightfooter
                                : "#636363"
                        }
                        backgroundCart={
                            config?.webappcolor.backgroundcart
                                ? config.webappcolor.backgroundcart
                                : "#FFBA49"
                        }
                        fontCart={
                            config?.webappcolor.fontcart
                                ? config.webappcolor.fontcart
                                : "#000"
                        }
                        noSales={
                            config &&
                                config?.content &&
                                config?.content.pdv &&
                                config?.content.pdv.noSales
                                ? config.content.pdv.noSales
                                : false
                        }
                    />
                )}
            </div>
        </div>
    );
}
